import React, { useState } from 'react';
import { X, DollarSign, Calendar, CreditCard, Save } from 'lucide-react';
import { API_URL } from '../../services/api';

const PaiementModal = ({ achat, fournisseur, onClose, onPaiementEnregistre }) => {
  const [montant, setMontant] = useState('');
  const [datePaiement, setDatePaiement] = useState(new Date().toISOString().split('T')[0]);
  const [modePaiement, setModePaiement] = useState('virement');
  const [numeroCheque, setNumeroCheque] = useState('');
  const [saving, setSaving] = useState(false);

  const soldeRestant = parseFloat(achat.solde_a_regler || achat.prix_achat_total || 0);
  const montantPaye = parseFloat(achat.montant_paye || 0);
  const prixTotal = parseFloat(achat.prix_achat_total || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const montantPaiement = parseFloat(montant);
    
    if (montantPaiement <= 0) {
      alert('Le montant doit être supérieur à 0');
      return;
    }
    
    if (montantPaiement > soldeRestant) {
      alert(`Le montant ne peut pas dépasser le solde restant (${soldeRestant.toFixed(2)} DH)`);
      return;
    }

    if (modePaiement === 'cheque' && !numeroCheque) {
      alert('Veuillez saisir le numéro de chèque');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/paiements/achats/${achat.id}/paiements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          montant: montantPaiement,
          date_paiement: datePaiement,
          mode_paiement: modePaiement,
          reference_paiement: modePaiement === 'cheque' ? numeroCheque : null
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Paiement enregistré avec succès !\nNouveau solde: ${result.solde_restant.toFixed(2)} DH`);
        onPaiementEnregistre();
        onClose();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement du paiement');
    } finally {
      setSaving(false);
    }
  };

  const payerTout = () => {
    setMontant(soldeRestant.toFixed(2));
  };

  const payerMoitie = () => {
    setMontant((soldeRestant / 2).toFixed(2));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] flex flex-col">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 flex justify-between items-center rounded-t-lg">
          <div className="flex items-center space-x-3">
            <DollarSign className="h-6 w-6" />
            <h3 className="text-xl font-bold">Enregistrer un paiement</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Informations de l'achat */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Référence :</span>
              <span className="font-semibold text-gray-900">{achat.reference}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Fournisseur :</span>
              <span className="font-semibold text-gray-900">{fournisseur?.nom || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Désignation :</span>
              <span className="font-semibold text-gray-900">{achat.designation}</span>
            </div>
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Prix total :</span>
                <span className="font-bold text-lg text-gray-900">{prixTotal.toFixed(2)} DH</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Déjà payé :</span>
                <span className="font-semibold text-green-600">{montantPaye.toFixed(2)} DH</span>
              </div>
              <div className="flex justify-between items-center border-t pt-2 mt-2">
                <span className="text-sm font-semibold text-gray-700">Solde restant :</span>
                <span className="font-bold text-xl text-red-600">{soldeRestant.toFixed(2)} DH</span>
              </div>
            </div>
          </div>

          {/* Formulaire de paiement */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant du paiement *
              </label>
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    value={montant}
                    onChange={(e) => setMontant(e.target.value)}
                    className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="0.00"
                    required
                    min="0.01"
                    max={soldeRestant}
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500 font-semibold">DH</span>
                </div>
              </div>
              <div className="flex space-x-2 mt-2">
                <button
                  type="button"
                  onClick={payerMoitie}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                >
                  50%
                </button>
                <button
                  type="button"
                  onClick={payerTout}
                  className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                >
                  Tout payer
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date du paiement *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={datePaiement}
                  onChange={(e) => setDatePaiement(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mode de paiement *
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <select
                  value={modePaiement}
                  onChange={(e) => {
                    setModePaiement(e.target.value);
                    if (e.target.value !== 'cheque') {
                      setNumeroCheque('');
                    }
                  }}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="virement">🏦 Virement bancaire</option>
                  <option value="cheque">📝 Chèque</option>
                  <option value="especes">💵 Espèces</option>
                  <option value="carte">💳 Carte bancaire</option>
                  <option value="credit">📋 Crédit</option>
                </select>
              </div>
            </div>

            {modePaiement === 'cheque' && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de chèque *
                </label>
                <input
                  type="text"
                  value={numeroCheque}
                  onChange={(e) => setNumeroCheque(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  placeholder="Ex: 1234567"
                  required
                />
              </div>
            )}

            {montant && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <div className="flex items-center">
                  <CreditCard className="h-5 w-5 text-blue-500 mr-2" />
                  <div className="text-sm">
                    <p className="font-semibold text-blue-800">
                      Nouveau solde après paiement : {(soldeRestant - parseFloat(montant || 0)).toFixed(2)} DH
                    </p>
                    {(soldeRestant - parseFloat(montant || 0)) === 0 && (
                      <p className="text-green-600 font-bold mt-1">✓ Achat entièrement payé</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || !montant}
              className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-5 w-5" />
              <span>{saving ? 'Enregistrement...' : 'Enregistrer le paiement'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaiementModal;
